import { CommonModule } from '@angular/common';
import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    input,
    model,
    NgZone,
    OnDestroy,
    OnInit,
    signal,
    viewChild,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiThumbnailContent } from '@features/media-library/interfaces';
import { ThumbnailItem, ThumbnailItemComponent } from '@ntv360/component-pantry';

/* ═══════════════════════════════════════════════════════
   TYPES & CONSTANTS
═══════════════════════════════════════════════════════ */

/**
 * Extends UI content with grid-specific logical metadata.
 */
interface Item extends UiThumbnailContent {
    /** The ID of the block this item belongs to, or null if it's a solo item. */
    block: string | null;
    /** Whether this item should span two columns (channel styling). */
    isChannel?: boolean;
}

/**
 * Visual configuration for a block group.
 */
interface BlockDef {
    /** The label displayed on the block overlay. */
    label: string;
    /** The theme color for the block's borders and backgrounds. */
    color: string;
}

/**
 * Represents a single non-grouped item in the grid flow.
 */
interface SlotSolo {
    type: 'solo';
    item: Item;
}

/**
 * Represents a group of items that are visually bonded.
 */
interface SlotBlock {
    type: 'block';
    blockId: string;
    items: Item[];
}

/**
 * Union type for grid slots.
 */
type Slot = SlotSolo | SlotBlock;

/**
 * Geometric and stylistic definition for row-segment overlays.
 */
interface OverlayDef {
    key: string;
    x: number;
    y: number;
    w: number;
    h: number;
    color: string;
    background: string;
    borderTop: string;
    borderBottom: string;
    borderLeft: string;
    borderRight: string;
    borderRadius: string;
    isFirst: boolean;
    label: string;
    blockId: string;
}

/**
 * Coordinates for the drag-drop ghost element.
 */
interface GhostPos {
    x: number;
    y: number;
}

/**
 * Static registry of available block types.
 */
const BLOCKS: Record<string, BlockDef> = {
    animal: { label: 'Animal Block · 180 sec', color: '#EF9F27' },
    space: { label: 'Space Block · 200 sec', color: '#378ADD' },
    food: { label: 'Food Block · 450 sec', color: '#E24B4A' },
};

/* ═══════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════ */

/**
 * Transforms flat items into grouped slots based on block assignments.
 * Consecutive items with the same block ID are clustered into a single SlotBlock.
 * @param arr Array of content items.
 * @returns Array of calculated grid slots.
 */
function buildSlots(arr: Item[]): Slot[] {
    const slots: Slot[] = [];
    let i = 0;
    while (i < arr.length) {
        const item = arr[i];
        if (!item.block) {
            slots.push({ type: 'solo', item });
            i++;
        } else {
            const blockId = item.block;
            const items: Item[] = [];
            while (i < arr.length && arr[i].block === blockId) {
                items.push(arr[i]);
                i++;
            }
            slots.push({ type: 'block', blockId, items });
        }
    }
    return slots;
}

/**
 * Flattens slots back into a linear sequence of items.
 * @param slots Array of grid slots.
 * @returns Linear array of content items.
 */
function slotsToArr(slots: Slot[]): Item[] {
    return slots.flatMap((s) => (s.type === 'solo' ? [s.item] : s.items));
}

/**
 * Handles slot reordering logic during drop operations.
 * @param slots Current slots.
 * @param fromIdx Original index.
 * @param toIdx Target index.
 * @param side Drop position relative to target.
 * @returns New reordered slots array.
 */
function moveSlot(slots: Slot[], fromIdx: number, toIdx: number, side: 'before' | 'after'): Slot[] {
    const result = [...slots];
    const moved = result.splice(fromIdx, 1)[0];
    let adjusted = toIdx > fromIdx ? toIdx - 1 : toIdx;
    if (side === 'after') adjusted += 1;
    result.splice(adjusted, 0, moved);
    return result;
}

/* ═══════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════ */

/**
 * ContentGridComponent
 *
 * A specialized grid layout for content assets that supports:
 * - Block grouping: Visual overlays and "lego-like" bonding for items in the same block.
 * - Multi-column items: Items can span multiple columns (specifically channel items).
 * - High-precision Drag-and-Drop: Reordering solo items or entire blocks.
 * - Responsive Overlay Engine: Dynamic calculation of absolute-positioned block borders.
 */
@Component({
    selector: 'app-content-grid',
    standalone: true,
    imports: [CommonModule, FormsModule, ThumbnailItemComponent],
    templateUrl: './content-grid.component.html',
    styleUrls: ['./content-grid.component.scss'],
})
export class ContentGridComponent implements OnDestroy, AfterViewInit, AfterViewChecked {
    /** Reference to the main grid container for measurement. */
    private gridRef = viewChild<ElementRef<HTMLDivElement>>('gridRef');

    /** Hidden canvas used to suppress default browser drag ghosts. */
    private blankCanvasRef = viewChild<ElementRef<HTMLCanvasElement>>('blankCanvas');

    /** The linear sequence of items in the grid. Reactive model for two-way binding. */
    public readonly items = model<Item[]>([]);

    /** Determines whether the items in the grid can be dragged. */
    public readonly draggable = input<boolean>(false);

    /** Current calculated number of columns in the grid layout. */
    private readonly cols = signal(6);

    /** Message for the temporary user feedback toast. */
    protected readonly toast = signal<string | null>(null);

    /** Internal timer for hiding the toast. */
    private toastTimer: ReturnType<typeof setTimeout> | undefined = undefined;

    /** Index of the slot being dragged. */
    protected readonly draggingSlotIdx = signal<number | null>(null);

    /** Index of the slot currently being hovered over during drag. */
    private readonly dropSlotIdx = signal<number | null>(null);

    /** Position (left or right) relative to the hovered slot. */
    private readonly dropSide = signal<'before' | 'after' | null>(null);

    /** Current screen coordinates of the custom drag ghost. */
    protected readonly ghostPos = signal<GhostPos>({ x: 0, y: 0 });

    /** Mouse offset relative to the item's top-left corner at drag start. */
    private ghostOffset: GhostPos = { x: 0, y: 0 };

    /** Calculated geometric data for all block row-segment overlays. */
    protected readonly overlays = signal<OverlayDef[]>([]);

    /** Pre-calculated row-segment boundary states for item styling. */
    private readonly itemStates = signal<
        Map<string, { isFirstInRowSegment: boolean; isLastInRowSegment: boolean }>
    >(new Map());

    /** Observer for detecting changes in the grid container's width. */
    private resizeObserver: ResizeObserver | null = null;

    /** Bound reference to the global dragover handler for event listener management. */
    private globalDragOver = (e: DragEvent) => this.onGlobalDragOver(e);

    /** Timer for the stabilizing second pass of overlay calculations. */
    private overlayUpdateTimer: ReturnType<typeof setTimeout> | undefined = undefined;

    /** Flag indicating that an overlay recalculation is required on the next tick. */
    private needsOverlayUpdate = false;

    /** Access to block definitions for template usage. */
    private readonly BLOCKS = BLOCKS;

    /**
     * Creates an instance of ContentGridComponent.
     * @param cdr Change detector for manual UI updates.
     * @param ngZone Angular zone for optimized event handling.
     */
    constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly ngZone: NgZone,
    ) {
        effect(() => {
            // Re-calculate overlays whenever items change
            this.items();
            this.triggerOverlayUpdate();
        });
    }

    /** Pre-calculates the slot structure whenever items change. */
    private readonly slots = computed(() => buildSlots(this.items()));

    /**
     * Component initialization. Triggers initial layout measurements.
     */
    public ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.setupResizeObserver();
            this.updateColumnCount();
            this.updateOverlays();
        });
    }

    /**
     * Lifecycle hook for post-render logic. Triggers overlay updates if flagged.
     */
    public ngAfterViewChecked(): void {
        if (this.needsOverlayUpdate) {
            this.needsOverlayUpdate = false;
            // defer to ensure DOM has reflowed
            requestAnimationFrame(() => this.updateOverlays());
        }
    }

    /**
     * Clean up observers and event listeners.
     */
    public ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        window.removeEventListener('dragover', this.globalDragOver);
        clearTimeout(this.toastTimer);
        clearTimeout(this.overlayUpdateTimer);
    }

    /**
     * Attaches an observer to detect container width changes.
     */
    private setupResizeObserver(): void {
        this.resizeObserver = new ResizeObserver(() => {
            this.updateColumnCount();
            this.triggerOverlayUpdate();
        });
        if (this.gridRef()?.nativeElement) {
            this.resizeObserver.observe(this.gridRef()!.nativeElement);
        }
    }

    /**
     * Probes the DOM to calculate current column count based on first row offsets.
     */
    private updateColumnCount(): void {
        const gridEl = this.gridRef()?.nativeElement;
        if (!gridEl) return;

        const children = gridEl.children;
        if (children.length === 0) return;

        // Find how many children are in the first row
        const firstChildTop = (children[0] as HTMLElement).offsetTop;
        let count = 0;
        for (let i = 0; i < children.length; i++) {
            if ((children[i] as HTMLElement).offsetTop === firstChildTop) {
                count++;
            } else {
                break;
            }
        }

        if (count > 0 && count !== this.cols()) {
            this.cols.set(count);
        }
    }

    /** Handles browser window resize events. */
    @HostListener('window:resize')
    protected onWindowResize(): void {
        this.updateColumnCount();
        this.triggerOverlayUpdate();
    }

    /** Handles manual column changes from the toolbar. */
    protected onColsChange(): void {
        this.triggerOverlayUpdate();
    }

    /**
     * Schedules an overlay recalculation for the next render window.
     * @param isSettle Whether this is the final stabilizing pass.
     */
    private triggerOverlayUpdate(isSettle = false): void {
        this.needsOverlayUpdate = true;
        this.cdr.detectChanges();

        // Optional second pass to ensure everything settled (mimicking React behavior)
        if (!isSettle) {
            clearTimeout(this.overlayUpdateTimer);
            this.overlayUpdateTimer = setTimeout(() => this.triggerOverlayUpdate(true), 150);
        }
    }

    /**
     * The core projection engine.
     * Calculates absolute screen coordinates for block row-segment overlays
     * by measuring the live DOM positions of every item in the grid.
     */
    private updateOverlays(): void {
        const gridEl = this.gridRef()?.nativeElement;
        if (!gridEl) return;

        // Use offsetParent for coordinate matching
        const ox = gridEl.offsetLeft;
        const oy = gridEl.offsetTop;

        const groups: Record<string, number[]> = {};
        this.items().forEach((item, i) => {
            if (!item.block) return;
            if (!groups[item.block]) groups[item.block] = [];
            groups[item.block].push(i);
        });

        const newOverlays: OverlayDef[] = [];

        Object.entries(groups).forEach(([blockId, indices]) => {
            const def = BLOCKS[blockId];
            if (!def) return;

            const rects = indices
                .map((i) => {
                    const outer = document.getElementById(`c${this.items()[i].id}`);
                    if (!outer) return null;

                    // Use offset-based values (transform-agnostic)
                    return {
                        id: this.items()[i].id,
                        left: outer.offsetLeft,
                        top: outer.offsetTop,
                        right: outer.offsetLeft + outer.offsetWidth,
                        bottom: outer.offsetTop + outer.offsetHeight,
                        offsetTop: outer.offsetTop,
                    };
                })
                .filter((r): r is NonNullable<typeof r> => r !== null);

            if (!rects.length) return;

            const rowMap: Record<number, typeof rects> = {};
            rects.forEach((r) => {
                if (!rowMap[r.offsetTop]) rowMap[r.offsetTop] = [];
                rowMap[r.offsetTop].push(r);
            });
            const rows = Object.values(rowMap).sort((a, b) => a[0].top - b[0].top);
            const PAD = 0;

            rows.forEach((row, ri) => {
                const isOnly = rows.length === 1;
                const isFirst = ri === 0;
                const isLast = ri === rows.length - 1;

                const x = row[0].left - PAD + ox;
                const y = row[0].top - PAD + oy;
                const w = row[row.length - 1].right - row[0].left + PAD * 2;
                const h = row[0].bottom - row[0].top + PAD * 2;

                const solid = `2px solid ${def.color}`;
                const dashed = `2px dashed ${def.color}`;

                newOverlays.push({
                    key: `${blockId}-${ri}`,
                    x,
                    y,
                    w,
                    h,
                    color: def.color,
                    background: `${def.color}12`,
                    borderTop: solid,
                    borderBottom: solid,
                    borderLeft: isOnly || isFirst ? solid : dashed,
                    borderRight: isOnly || isLast ? solid : dashed,
                    borderRadius: isOnly
                        ? '10px'
                        : isFirst
                          ? '10px 0 0 10px'
                          : isLast
                            ? '0 10px 10px 0'
                            : '0',
                    isFirst,
                    label: def.label,
                    blockId,
                });
            });
        });

        this.overlays.set(newOverlays);

        // Calculate item states for styling (row segment boundaries)
        const newItemStates = new Map<
            string,
            { isFirstInRowSegment: boolean; isLastInRowSegment: boolean }
        >();
        this.items().forEach((item, i) => {
            if (!item.block) return;
            const slot = this.getSlotForItem(item) as SlotBlock;
            if (!slot) return;

            const outer = document.getElementById(`c${item.id}`);
            if (!outer) return;

            const currentTop = outer.offsetTop;

            // Find items in the same block and same row
            const sameRowItems = slot.items.filter((it) => {
                const el = document.getElementById(`c${it.id}`);
                return el && el.offsetTop === currentTop;
            });

            const isFirstInRowSegment = sameRowItems[0].id === item.id;
            const isLastInRowSegment = sameRowItems[sameRowItems.length - 1].id === item.id;

            newItemStates.set(item.id, { isFirstInRowSegment, isLastInRowSegment });
        });
        this.itemStates.set(newItemStates);

        // get grid gap
        const gridGap = parseFloat(getComputedStyle(gridEl).gap ?? 20);

        // Calculate a more accurate scale factor for block items
        const gridWidth = gridEl.offsetWidth;
        const estimatedCellWidth = gridWidth / this.cols();
        const scaleX = (estimatedCellWidth - gridGap) / estimatedCellWidth;
        gridEl.style.setProperty('--item-scale-x', scaleX.toFixed(4));

        const firstCell = gridEl.querySelector('.cell') as HTMLElement;
        if (firstCell) {
            const cellHeight = firstCell.offsetHeight;
            const scaleY = (cellHeight - gridGap * 2) / cellHeight;
            gridEl.style.setProperty('--item-scale-y', scaleY.toFixed(4));
        }

        this.cdr.detectChanges();
    }

    // ── Slot helpers ──

    /**
     * Resolves the slot index for a specific item.
     * @param item The content item.
     * @returns Index in the slots signal.
     */
    protected getSlotIdx(item: Item): number {
        return this.slots().findIndex((s) =>
            s.type === 'solo' ? s.item === item : s.items.includes(item),
        );
    }

    /**
     * Returns the slot object containing the specified item.
     * @param item The content item.
     * @returns The slot (Solo or Block).
     */
    private getSlotForItem(item: Item): Slot | undefined {
        return this.slots().find((s) =>
            s.type === 'solo' ? s.item === item : s.items.includes(item),
        );
    }

    /**
     * Returns the Block slot if the item is part of a block.
     * @param item The content item.
     * @returns SlotBlock or undefined.
     */
    private getBlockForItem(item: Item): SlotBlock | undefined {
        const s = this.slots().find(
            (s) => s.type === 'block' && (s as SlotBlock).items.includes(item),
        );
        return s as SlotBlock | undefined;
    }

    /** Checks if item is the leading edge of its slot. */
    private isFirstInSlot(item: Item): boolean {
        const slot = this.getSlotForItem(item);
        if (!slot) return false;
        return slot.type === 'block' ? slot.items[0] === item : false;
    }

    /** Checks if item is the trailing edge of its slot. */
    private isLastInSlot(item: Item): boolean {
        const slot = this.getSlotForItem(item);
        if (!slot) return false;
        return slot.type === 'block' ? slot.items[slot.items.length - 1] === item : false;
    }

    /** Checks if item is the leftmost element in its block's row segment. */
    private isBlockFirstInRowSegment(item: Item): boolean {
        return this.itemStates().get(item.id)?.isFirstInRowSegment ?? false;
    }

    /** Checks if item is the rightmost element in its block's row segment. */
    private isBlockLastInRowSegment(item: Item): boolean {
        return this.itemStates().get(item.id)?.isLastInRowSegment ?? false;
    }

    /** Determines if an item should show a drop highlight. */
    private getHighlightSide(item: Item): 'before' | 'after' | null {
        const slotIdx = this.getSlotIdx(item);
        if (this.dropSlotIdx() !== slotIdx) return null;
        const slot = this.slots()[slotIdx];
        const itemsInTarget = slot.type === 'block' ? slot.items : [slot.item];
        const isFirst = itemsInTarget[0] === item;
        const isLast = itemsInTarget[itemsInTarget.length - 1] === item;
        if (this.dropSide() === 'before' && isFirst) return 'before';
        if (this.dropSide() === 'after' && isLast) return 'after';
        return null;
    }

    /** Checks if item belongs to the slot currently being dragged. */
    private isDragging(item: Item): boolean {
        return this.draggingSlotIdx() === this.getSlotIdx(item);
    }

    /** Checks if the item's whole block is being dragged. */
    private isBlockDragging(item: Item): boolean {
        if (this.draggingSlotIdx() === null) return false;
        const draggingSlot = this.slots()[this.draggingSlotIdx()!];
        if (draggingSlot?.type !== 'block') return false;
        const block = this.getBlockForItem(item);
        return draggingSlot === block;
    }

    /** Returns the theme border color for block items. */
    protected getCellBorderColor(item: Item): string | undefined {
        return item.block ? `${BLOCKS[item.block].color}55` : undefined;
    }

    /** Dynamic CSS variables for the grid (e.g. column count). */
    protected getCssVars(): Record<string, string> {
        return { '--columns': String(this.cols()) };
    }

    /** Returns an exhaustive map of CSS classes for a grid cell. */
    protected getCellClasses(item: Item): Record<string, boolean> {
        const slotIdx = this.getSlotIdx(item);
        const isDragging = this.draggingSlotIdx() === slotIdx;
        const isBlockDragging = this.isBlockDragging(item);
        const highlightSide = this.getHighlightSide(item);
        const block = this.getBlockForItem(item);
        const isFirstInSlot = this.isFirstInSlot(item);
        const isLastInSlot = this.isLastInSlot(item);
        const isFirstInRowSeg = !!block && this.isBlockFirstInRowSegment(item);
        const isLastInRowSeg = !!block && this.isBlockLastInRowSegment(item);

        return {
            cell: true,
            'is-draggable': this.draggable(),
            'block-dragging': isDragging || isBlockDragging,
            'solo-dragging': !isBlockDragging && isDragging,
            'drop-before': highlightSide === 'before',
            'drop-after': highlightSide === 'after',
            'in-block': !!block,
            'first-in-slot': isFirstInSlot,
            'last-in-slot': isLastInSlot,
            'is-first-in-row-segment': isFirstInRowSeg,
            'is-last-in-row-segment': isLastInRowSeg,
            'is-channel': !!item.isChannel,
        };
    }

    // ── Drag Handlers ──

    /**
     * Initializes the drag sequence.
     * Sets up custom ghosts and global dragover listeners.
     */
    protected onDragStart(e: DragEvent, item: Item): void {
        if (!this.draggable()) {
            e.preventDefault();
            return;
        }

        const slotIdx = this.getSlotIdx(item);
        this.draggingSlotIdx.set(slotIdx);

        if (this.blankCanvasRef()?.nativeElement) {
            e.dataTransfer!.setDragImage(this.blankCanvasRef()!.nativeElement, 0, 0);
        }
        e.dataTransfer!.effectAllowed = 'move';

        const div = e.currentTarget as HTMLElement;
        const rect = div.getBoundingClientRect();
        this.ghostOffset = {
            x: e.clientX - rect.left + 10,
            y: e.clientY - rect.top + 10,
        };
        this.ghostPos.set({ x: e.clientX, y: e.clientY });

        this.ngZone.runOutsideAngular(() => {
            window.addEventListener('dragover', this.globalDragOver);
        });
    }

    /**
     * Global dragover handler for real-time ghost positioning and drop target calculation.
     */
    private onGlobalDragOver(e: DragEvent): void {
        e.preventDefault();
        this.ngZone.run(() => {
            if (this.draggingSlotIdx() === null) return;
            this.ghostPos.set({ x: e.clientX, y: e.clientY });

            const target = (e.target as HTMLElement).closest('.cell') as HTMLElement;
            if (!target) {
                this.dropSlotIdx.set(null);
                this.dropSide.set(null);
                return;
            }

            const targetSlotIdx = parseInt(target.dataset['slotIdx'] || '-1');
            if (targetSlotIdx === -1 || targetSlotIdx === this.draggingSlotIdx()) {
                this.dropSlotIdx.set(null);
                this.dropSide.set(null);
                return;
            }

            const rect = target.getBoundingClientRect();
            const side = e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
            this.dropSlotIdx.set(targetSlotIdx);
            this.dropSide.set(side);
        });
    }

    /** Resets dragging state and clean up listeners. */
    protected onDragEnd(): void {
        this.draggingSlotIdx.set(null);
        this.dropSlotIdx.set(null);
        this.dropSide.set(null);
        window.removeEventListener('dragover', this.globalDragOver);
    }

    /** Finalizes the move operation and updates the items model. */
    onDrop(e: DragEvent): void {
        e.preventDefault();
        if (this.draggingSlotIdx() === null || this.dropSlotIdx() === null || !this.dropSide())
            return;

        const currentSlots = this.slots();
        const slot = currentSlots[this.draggingSlotIdx()!];
        const label = slot.type === 'block' ? BLOCKS[slot.blockId].label : `"${slot.item.name}"`;

        const nextSlots = moveSlot(
            currentSlots,
            this.draggingSlotIdx()!,
            this.dropSlotIdx()!,
            this.dropSide()!,
        );
        this.items.set(slotsToArr(nextSlots));
        this.showToast(`${label} moved`);
        this.onDragEnd();

        this.triggerOverlayUpdate();
    }

    /**
     * Prevents default browser behavior during grid-wide dragover to allow drop operations.
     * @param e The drag event.
     */
    onGridDragOver(e: DragEvent): void {
        e.preventDefault();
    }

    // ── Toast ──

    /** Displays a temporary feedback notification. */
    showToast(msg: string): void {
        this.toast.set(msg);
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => {
            this.toast.set(null);
            this.cdr.detectChanges();
        }, 2500);
    }

    // ── Ghost helpers ──

    /** The slot currently being rendered in the ghost. */
    get ghostSlot(): Slot | null {
        if (this.draggingSlotIdx() === null) return null;
        return this.slots()[this.draggingSlotIdx()!] ?? null;
    }

    /** Inline styles for the floating drag ghost. */
    get ghostStyle(): Record<string, string> {
        return {
            left: `${this.ghostPos().x - this.ghostOffset.x}px`,
            top: `${this.ghostPos().y - this.ghostOffset.y}px`,
            display: 'block',
        };
    }

    /** Grid layout logic for the ghost preview (capped at 4 columns). */
    protected ghostGhostGridCols(slot: SlotBlock): string {
        const GHOST_COLS = Math.min(this.cols(), 4);
        return `repeat(${GHOST_COLS}, 130px)`;
    }

    /** Subset of items to show in a block ghost preview. */
    protected ghostShownItems(slot: SlotBlock): Item[] {
        return slot.items.slice(0, 8);
    }

    /** Count of items hidden in the block ghost preview. */
    protected ghostExtraCount(slot: SlotBlock): number {
        return slot.items.length - this.ghostShownItems(slot).length;
    }

    /** Helper for retrieving block metadata. */
    protected getBlockDef(blockId: string): BlockDef {
        return BLOCKS[blockId];
    }

    /** TrackBy function for item iteration. */
    protected trackById(_: number, item: Item): string {
        return item.id;
    }

    /** TrackBy function for overlay iteration. */
    protected trackByKey(_: number, overlay: OverlayDef): string {
        return overlay.key;
    }
}
